import { QuickscreenOfferResponse } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { useQueryClient } from 'react-query';
import { useCareCreditIntegrationV1QueryUpdaters } from '../query-updaters';
import type { VerifyCareCreditPersonIO } from '../types';
import {
  type UseCareCreditIntegrationV1MutationEndpointArgs,
  useCareCreditIntegrationV1Mutation,
} from './use-care-credit-integration-v1-mutation';

type MutationContext<C = unknown> = {
  otherContext?: C;
};

const PERSISTED_CARE_CREDIT_STATUSES = new Set(['CARDHOLDER', 'PREAPPROVED', 'PREQUALIFICATION_NEEDED']);

export const shouldInvalidateCareCreditPerson = (response: VerifyCareCreditPersonIO['output']) =>
  PERSISTED_CARE_CREDIT_STATUSES.has(response.weaveStatus ?? '') ||
  response.decisionMessage === 'APPROVED' ||
  response.errorResponse?.errorCode === 'credit.application.accountAlreadyExist';

/**
 * A hook that verifies a patient's CareCredit data and refreshes the normalized person query after persistence.
 */
export const useVerifyCareCreditPersonMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends VerifyCareCreditPersonIO['input'] = VerifyCareCreditPersonIO['input']
>({
  options,
  ...args
}: UseCareCreditIntegrationV1MutationEndpointArgs<
  'VerifyCareCreditPerson',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateQueries } = useCareCreditIntegrationV1QueryUpdaters();

  const queryClient = useQueryClient();

  return useCareCreditIntegrationV1Mutation<
    'VerifyCareCreditPerson',
    E,
    MutationContext<C>,
    OtherOptions,
    RequestOverride
  >({
    endpointName: 'VerifyCareCreditPerson',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => ({
        otherContext: await options?.onMutate?.(request),
      }),
      onSuccess: async (response, request, context) => {
        if (shouldInvalidateCareCreditPerson(response)) {
          await invalidateQueries({
            endpointName: 'GetCareCreditPerson',
            queryFilters: {
              predicate: ({ queryKey }) => {
                const cachedRequest = queryKey[2];
                return (
                  cachedRequest.locationId === request.locationId &&
                  cachedRequest.weavePersonId === request.weavePersonId
                );
              },
            },
          });
        }

        if (response.weaveStatus !== 'UNKNOWN') {
          /**
           * Cannot use generic updater because it's a custom query wrapping a mutation
           */
          queryClient.getQueriesData<QuickscreenOfferResponse>('careCreditPatientData').forEach(([queryKey]) => {
            if (
              Array.isArray(queryKey) &&
              queryKey[1] === request.locationId &&
              queryKey[3] === request.weavePersonId
            ) {
              queryClient.setQueryData(queryKey, response);
            }
          });
        }

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => options?.onError?.(error, request, context?.otherContext),
      onSettled: (response, error, request, context) =>
        options?.onSettled?.(response, error, request, context?.otherContext),
    },
  });
};
